import Handsontable from "handsontable";
import {CellSettings} from "handsontable/settings";
import {VersionType} from "../../../../__generated__/generated_types";
import {Property, propertyDisplayBudgetYear} from "../../../../contexts/properties/logic";
import {RenovationData} from "../AnalystRenovations";
import {UnitTypeWithRenovationPackages} from "../AnalystRenovationsTable";
import * as css from "../styles/reno-table.module.scss";


export interface RenovationTableRow {
    label: string;
    rowType: "property" | "unitType" | "renoPackage" | "totalRenovations" | "totalExpiringMoveOuts";
    rfcstValues: (number | null)[];
    bdgtValues: (number | null)[];
    rowIndex: number;
    // used for determining rows to show/hide
    childRowIndexes?: number[];
    // used to determine which rows contribute to unit type totals
    colSumRowIndexes?: number[];
    rowId?: string;
    parentRowIndex?: number;
    hasPackages?: boolean;
    premium?: number;
    cost?: number;
}

export const RFCST_TOTAL_COL_INDEX = 13;
export const BDGT_TOTAL_COL_INDEX = 26;

// builds the data for the reno table, as well as the default hidden rows and column summary
// configurations used by handsontable. building all three here helps limit iterations over the data
export function buildRenoTableData(
    unitTypes: UnitTypeWithRenovationPackages[],
    property: Property,
    expandedUnitTypeIds: Set<string>,
): {
    tableData: RenovationTableRow[],
    hiddenRows: number[],
    unitTypeRowIndexes: Set<number>,
    propertySummaryContributionIndexes: Set<number>,
} {
    const tableData: RenovationTableRow[] = [];
    const hiddenRows: number[] = [];
    const propertyLevelSummaryTotalRanges: number[][] = [];
    const unitTypeRowIndexes = new Set<number>();
    const propertySummaryContributionIndexes = new Set<number>();

    let currentRow = 0;
    const propertyRow: RenovationTableRow = {
        label: property.name,
        rowType: "property",
        rfcstValues: new Array(12).fill(null),
        bdgtValues: new Array(12).fill(null),
        rowIndex: currentRow,
    };
    tableData.push(propertyRow);

    for (const unitType of unitTypes) {
        currentRow += 1;
        const unitTypeRowConfig: RenovationTableRow = {
            label: unitType.name,
            rowType: "unitType",
            rfcstValues: new Array(12).fill(null),
            bdgtValues: new Array(12).fill(null),
            rowId: unitType.id,
            rowIndex: currentRow,
            hasPackages: unitType.renovationPackages.length > 0,
        };

        if (unitTypeRowConfig.hasPackages) {
            unitTypeRowConfig.childRowIndexes = [];
            unitTypeRowConfig.colSumRowIndexes = [];

            // the + 2 accounts for total and expiring move outs rows
            const sumRowContributors = currentRow + unitType.renovationPackages.length;
            const childRowEnd = sumRowContributors + 2;
            for (let i = currentRow + 1; i <= childRowEnd; i++) {
                if (i <= sumRowContributors) {
                    unitTypeRowConfig.colSumRowIndexes.push(i);
                    propertySummaryContributionIndexes.add(i);
                }
                unitTypeRowConfig.childRowIndexes.push(i);
            }
        }

        tableData.push(unitTypeRowConfig);
        unitTypeRowIndexes.add(unitTypeRowConfig.rowIndex);

        const totalRfcstRenovationsByMonth = new Array(12).fill(0);
        const totalBdgtRenovationsByMonth = new Array(12).fill(0);

        if (unitType.renovationPackages.length > 0) {
            const unitTypePackagesRowStart = currentRow + 1;
            const unitTypePackagesRowEnd = currentRow + unitType.renovationPackages.length;
            propertyLevelSummaryTotalRanges.push([unitTypePackagesRowStart, unitTypePackagesRowEnd]);

            for (const renoPackage of unitType.renovationPackages) {
                const rfcstData = renoPackage.renovationNumbers.find(x => x.versionType == VersionType.Reforecast && x.year == property.reforecastYear)?.expected.sortBy("monthIndex");
                const bdgtData = renoPackage.renovationNumbers.find(x => x.versionType == VersionType.Budget && x.year == (property.reforecastYear + 1))?.expected.sortBy("monthIndex");
                const rfcstValues = rfcstData?.map(x => x.value == null ? null : +x.value) ?? new Array(12).fill(null);
                const bdgtValues = bdgtData?.map(x => x.value == null ? null : +x.value) ?? new Array(12).fill(null);

                for (let monthIndex = 0; monthIndex < rfcstValues.length; monthIndex++) {
                    propertyRow.rfcstValues[monthIndex] += rfcstValues[monthIndex];
                    propertyRow.bdgtValues[monthIndex] += bdgtValues[monthIndex];
                    totalRfcstRenovationsByMonth[monthIndex] += rfcstValues[monthIndex];
                    totalBdgtRenovationsByMonth[monthIndex] += bdgtValues[monthIndex];
                }

                const totalCost = renoPackage.costCategoryMappings?.map(c => Number(c.amount ?? 0)).sum() ?? 0;

                currentRow += 1;
                tableData.push({
                    label: renoPackage.name,
                    rowType: "renoPackage",
                    rfcstValues: rfcstValues,
                    bdgtValues: bdgtValues,
                    rowId: renoPackage.id,
                    parentRowIndex: unitTypeRowConfig.rowIndex,
                    rowIndex: currentRow,
                    premium: renoPackage.premium,
                    cost: totalCost,
                });
                if (!expandedUnitTypeIds.has(unitType.id)) {
                    hiddenRows.push(currentRow);
                }
            }

            currentRow += 1;
            tableData.push({
                label: "Total Renovations",
                rowType: "totalRenovations",
                rfcstValues: totalRfcstRenovationsByMonth,
                bdgtValues: totalBdgtRenovationsByMonth,
                rowId: unitType.id,
                rowIndex: currentRow,
                colSumRowIndexes: unitTypeRowConfig.colSumRowIndexes,
            });
            if (!expandedUnitTypeIds.has(unitType.id)) {
                hiddenRows.push(currentRow);
            }

            const {rfcstValues, bdgtValues} = getExpiringMoveOuts(unitType, property);

            currentRow += 1;
            tableData.push({
                label: "Total Expiring Move Outs",
                rowType: "totalExpiringMoveOuts",
                rfcstValues,
                bdgtValues,
                rowId: unitType.id,
                rowIndex: currentRow,
            });
            if (!expandedUnitTypeIds.has(unitType.id)) {
                hiddenRows.push(currentRow);
            }
        }
    }

    return {tableData, hiddenRows, unitTypeRowIndexes, propertySummaryContributionIndexes: propertySummaryContributionIndexes};
}

// build the array of cell config options for the table cell callback function
// since the cell function doesn't support any kind of ranges, we need to identify
// every individual cell that is going to need any kind of configuration
export function buildCellConfig(data: RenovationTableRow[], expandedUnitTypeIds: Set<string>, property: Property, editAllowed: boolean): CellSettings[] {
    const cellConfigMap: Map<string, CellSettings> = new Map();

    for (const rowData of data) {
        const rowType = data[rowData.rowIndex]?.rowType ?? "";

        // style rfcst and bdgt total columns
        updateCellConfig(rowData.rowIndex, RFCST_TOTAL_COL_INDEX, cellConfigMap, {className: css.greyCell});
        updateCellConfig(rowData.rowIndex, BDGT_TOTAL_COL_INDEX, cellConfigMap, {className: css.greyCell});

        // add our bar showing reforecast start month
        updateCellConfig(rowData.rowIndex, property.reforecastStartMonthIndex + 1, cellConfigMap, {className: css.blueBorderLeft});

        for (let colIndex = 0; colIndex <= BDGT_TOTAL_COL_INDEX; colIndex++) {
            if (rowData.rowIndex == 0) {
                updateCellConfig(0, colIndex, cellConfigMap, {className: css.thickBorderBottom});
            }

            if (rowData.rowType != "totalExpiringMoveOuts" && colIndex > 0 && colIndex != RFCST_TOTAL_COL_INDEX && colIndex != BDGT_TOTAL_COL_INDEX) {
                updateCellConfig(rowData.rowIndex, colIndex, cellConfigMap, {
                    renderer: (
                        instance,
                        TD,
                        row,
                        col,
                        prop,
                        value,
                        cellProperties,
                    ) => {
                        const adjustedDisplayValue = value == null || value === "" || value == 0 ? null : Math.round(value);

                        Handsontable.renderers.NumericRenderer(
                            instance,
                            TD,
                            row,
                            col,
                            prop,
                            adjustedDisplayValue,
                            cellProperties,
                        );
                    }
                });
            }

            if (rowType == "unitType") {
                // keeps the grey border on the rfcst and bdgt total columns when a unitType row is expanded
                updateCellConfig(rowData.rowIndex, RFCST_TOTAL_COL_INDEX - 1, cellConfigMap, {className: css.greyBorder});
                updateCellConfig(rowData.rowIndex, RFCST_TOTAL_COL_INDEX, cellConfigMap, {className: css.greyBorder});
                updateCellConfig(rowData.rowIndex, BDGT_TOTAL_COL_INDEX - 1, cellConfigMap, {className: css.greyBorder});
                updateCellConfig(rowData.rowIndex, BDGT_TOTAL_COL_INDEX, cellConfigMap, {className: css.greyBorder});

                if (rowData.rowId && expandedUnitTypeIds.has(rowData.rowId)) {
                    updateCellConfig(rowData.rowIndex, colIndex, cellConfigMap, {className: css.hiddenValue, disableVisualSelection: true});
                }
            }

            if (rowType == "renoPackage" && colIndex > 0 && colIndex != RFCST_TOTAL_COL_INDEX && colIndex != BDGT_TOTAL_COL_INDEX && editAllowed) {
                updateCellConfig(rowData.rowIndex, colIndex, cellConfigMap, {className: css.editableCell, readOnly: false});
            }

            if (rowType == "totalRenovations") {
                updateCellConfig(rowData.rowIndex, colIndex, cellConfigMap, {className: css.thickBorderTop});
            }

            if (rowType == "totalExpiringMoveOuts" && colIndex > 0) {
                updateCellConfig(rowData.rowIndex, colIndex, cellConfigMap, {numericFormat: {pattern: "0.0"}});
            }

            if (rowType == "totalExpiringMoveOuts" || rowType == "totalRenovations") {
                updateCellConfig(rowData.rowIndex, colIndex, cellConfigMap, {className: css.greyCell});
            }
        }
    }

    return Array.from(cellConfigMap.values());
}

// handsontable CellSettings updates overwrite existing classNames, so this
// method allows you to build up your CellSettings objects, adding to className
// and appending settings to entries with the same row/col coordinates
// the cellSettings object here is keyed by strings of row_col
function updateCellConfig(row: number, col: number, cellSettings: Map<string, CellSettings>, newSettings: Partial<CellSettings>): void {
    const coords = `${row}_${col}`;
    const existingSettings = cellSettings.get(coords);
    let className = existingSettings?.className || "";

    if (newSettings.className) {
        className += ` ${newSettings.className}`;
    }

    cellSettings.set(coords, {
        row,
        col,
        ...existingSettings,
        ...newSettings,
        className,
    });
}

// hides the values in the unitType row and styles it when expanded
export function hideValuesInRow(rowIndex: number, hot: Handsontable): void {
    for (let colIndex = 1; colIndex < 27; colIndex++) {
        const cellMeta = hot.getCellMeta(rowIndex, colIndex);
        hot.setCellMetaObject(rowIndex, colIndex, {
            ...cellMeta,
            className: `${cellMeta.className} ${css.hiddenValue}`,
            disableVisualSelection: true,
        });
    }
}

// there is a bug with setting className as string[], so we always want to set with string
export function showValuesInRow(rowIndex: number, hot: Handsontable): void {
    for (let colIndex = 1; colIndex < 27; colIndex++) {
        const cellMeta = hot.getCellMeta(rowIndex, colIndex);
        let existingClassName = cellMeta.className ?? "";

        if (Array.isArray(existingClassName)) {
            existingClassName = existingClassName.join(" ");
        }

        hot.setCellMetaObject(rowIndex, colIndex, {
            ...cellMeta,
            className: existingClassName.replace(css.hiddenValue, ""),
            disableVisualSelection: false,
        });
    }
}

function getExpiringMoveOuts(unitType: UnitTypeWithRenovationPackages, property: Property): {rfcstValues: number[], bdgtValues: number[]} {
    const rfcstValues = new Array(12).fill(0);
    const bdgtValues = new Array(12).fill(0);
    const utsvEntries = unitType.unitTypeScheduleValues;

    if (utsvEntries) {
        const utsvValuesByVersion = utsvEntries?.groupBy(x => x.version);
        const actuals = utsvValuesByVersion[VersionType.Actuals]?.filter(x => x.year == property.reforecastYear).sortBy("monthIndex").map(x => x.moveOutRate * x.leaseExpirationCount);
        const rfcst = utsvValuesByVersion[VersionType.Reforecast]?.sortBy("monthIndex").map(x => x.moveOutRate * x.leaseExpirationCount);
        const bdgt = utsvValuesByVersion[VersionType.Budget]?.sortBy("monthIndex").map(x => x.moveOutRate * x.leaseExpirationCount);

        for (let i = 0; i < 12; i++) {
            rfcstValues[i] = i < property.reforecastStartMonthIndex ? actuals?.[i] : rfcst?.[i];
            bdgtValues[i] = bdgt?.[i];
        }
    }

    return {rfcstValues, bdgtValues};
}

export function buildColHeaderHtml(label: string, colIndex: number, property: Property): string {
    const showBlueBackground = colIndex > property.reforecastStartMonthIndex && colIndex != 13 && colIndex != 26;
    const showRfcstBorder = colIndex == property.reforecastStartMonthIndex + 1;
    const showYear = colIndex == 1 || colIndex == 14;
    let yearToShow = propertyDisplayBudgetYear(property) - 1;

    let versionType = "Actual";
    if (colIndex > property.reforecastStartMonthIndex) {
        versionType = "Rfcst";
    }

    if (colIndex > 13) {
        versionType = "Bdgt";
        yearToShow += 1;
    }

    return `
        <div class="${css.colHeaderInner} ${showRfcstBorder && css.blueBorderLeft} ${showBlueBackground && css.blueCell}">
            <span class="${css.colHeaderVersion}">${versionType}</span>
            <span class="${css.colHeaderYear} ${!showYear && css.isHidden}">
                ${yearToShow}
            </span>
            <span class="${css.colHeaderMonth}">${label}<span>
        </div>
    `;
}

export function summarizeUnitTypePackageCounts(parentRowIndexes: Set<number>, colsToSummarize: Set<number>, hot: Handsontable): [number, number, (number | null)][] {
    const updatesToMake: [number, number, (number | null)][] = [];

    for (const parentRowIndex of Array.from(parentRowIndexes)) {
        const rowData: RenovationTableRow | undefined = hot.getSourceDataAtRow(parentRowIndex) as RenovationTableRow | undefined;
        const colSumRowIndexes = rowData?.colSumRowIndexes;

        if (colSumRowIndexes) {
            for (const colToSummarize of Array.from(colsToSummarize)) {
                const colValues = hot.getSourceDataAtCol(colToSummarize);
                const summaryValue = colSumRowIndexes.map(x => +(colValues[x] ?? 0)).sum();

                updatesToMake.push(
                    [parentRowIndex, colToSummarize, summaryValue],
                    [parentRowIndex + colSumRowIndexes.length + 1, colToSummarize, summaryValue],
                );
            }
        }
    }

    return updatesToMake;
}

export function summarizePropertyColumns(rowsToSummarize: Set<number>, colsToSummarize: Set<number>, hot: Handsontable): [number, number, (number | null)][] {
    const updatesToMake: [number, number, (number | null)][] = [];

    for (const colToSummarize of Array.from(colsToSummarize)) {
        const colValues = hot.getSourceDataAtCol(colToSummarize);
        const summaryValue = Array.from(rowsToSummarize).map(x => +(colValues[x] ?? 0)).sum();

        updatesToMake.push([0, colToSummarize, summaryValue]);
    }

    return updatesToMake;
}

// returns null if no errors, or a string with the error text to display:
// "Entries cannot be text", "Entries cannot be decimals or negative.", etc.
export function checkForValidationError(changes: (string | null | number)[]): string | null {
    const errorTypes = [];

    for (let change of changes) {
        // empty string values should be treated as nulls
        if (change == "") {
            change = null;
        }

        if (change != null) {
            if (isNaN(+change)) {
                errorTypes.push("text");
            } else {
                if (!Number.isInteger(+change)) {
                    errorTypes.push("decimals");
                }

                if (+change < 0) {
                    errorTypes.push("negative");
                }
            }
        }
    }

    if (errorTypes.length) {
        let builtErrorMessage = "Entries cannot be ";

        for (const [index, errorType] of Array.from(errorTypes.entries())) {
            if (index == 0) {
                builtErrorMessage += errorType;
            } else if (index == errorTypes.length - 1) {
                builtErrorMessage += ` or ${errorType}`;
            } else {
                builtErrorMessage += `, ${errorType}`;
            }
        }
        return builtErrorMessage;
    }

    return null;
}

export function transformRenoApiDataForTableUse(renovationData: RenovationData): UnitTypeWithRenovationPackages[] {
    const unitTypesForProperty = renovationData.propertyData[0]?.unitTypesCustom;

    if (!unitTypesForProperty) {
        return [];
    }

    const unitTypesWithRenoData: UnitTypeWithRenovationPackages[] = unitTypesForProperty.sortBy("name").map(ut => {
        const renoPackagesForUnitType = renovationData.renovationPackageData?.filter(x => x.unitTypeId == ut.id).sortBy("createdAt") ?? [];
        const builtUnitTypeData: UnitTypeWithRenovationPackages = {
            ...ut,
            renovationPackages: renoPackagesForUnitType,
            unitTypeScheduleValues: [],
        };

        if (renoPackagesForUnitType.length > 0) {
            // api brings back utsv duplicated across packages, but we want it one level up on the unit type
            // so, we can grab them from any of the renovation packages attached to the unit type
            const utsvEntries = renoPackagesForUnitType[0]?.unitTypeScheduleValues;

            if (utsvEntries) {
                builtUnitTypeData.unitTypeScheduleValues = utsvEntries.map(utsv => ({
                    leaseExpirationCount: utsv.leaseExpirationCount,
                    monthIndex: utsv.monthIndex,
                    moveOutCount: utsv.moveOutCount,
                    moveOutRate: utsv.moveOutRate,
                    year: utsv.version.year,
                    version: utsv.version.type,
                }));
            }
        }

        return builtUnitTypeData;
    });

    return unitTypesWithRenoData;
}
